/* ============================================================================
   Merchant Setup → Zone Mapping — shared meta for the three pages
   (Zone Lookup, Zone Override, Base Zone Upload).

   The merchant base zone map lives in Mongo zones_map_1..9 under the pseudo
   courier id PROZO_Z_MAP (one row per pickup→drop lane). Per-merchant overrides
   live in MySQL merchant_zone_overrides. Both are read by merchant pricing;
   courier zones are a different store (S3) and are NOT what these pages edit.
   ========================================================================== */

/** Pseudo courier id the backend uses for the merchant base zone map (ServicabilityZone.PROZO_ZONE_CP_ID). */
export const MERCHANT_ZONE_CP_ID = 'PROZO_Z_MAP'

/** Zones a merchant lane can be overridden to (the 5 Prozo zones). */
export const MERCHANT_ZONES = [
  {value: '1', label: 'Local'},
  {value: '2', label: 'Regional'},
  {value: '3', label: 'Metro'},
  {value: '4', label: 'ROI'},
  {value: '5', label: 'Special'},
]

/** Every zone code the lookup API can return (courier rows use courier-specific codes too). */
const ZONE_CODE_NAMES = {
  0: 'NOT_SERVICEABLE',
  1: 'LOCAL',
  2: 'REGIONAL',
  3: 'METROS',
  4: 'ROI',
  5: 'SPECIAL',
  6: 'WITHIN_REGIONAL_ROS',
  7: 'WITHIN_REGIONAL_UP',
  8: 'REST_OF_INDIA_ROS',
  9: 'REST_OF_INDIA_UP',
  10: 'SPECIAL_ZONE_ROS',
  11: 'SPECIAL_ZONE_UP',
  12: 'NORTH_EAST',
  13: 'NORTH_EAST_ROS',
  14: 'NORTH_EAST_UP',
  15: 'METRO_C2_DL',
  16: 'REST_OF_INDIA_D2_DL',
  17: 'NTH_EAST_JMU_HP_DL',
  18: 'ZONE_F',
  19: 'SPECIAL_DESTINATION',
  20: 'ROI_A',
  21: 'ROI_B',
  22: 'WITHIN_ZONE',
  101: 'INTRA_CITY_SDL',
  102: 'WITHIN_ZONES_SDL',
  103: 'METRO_SDL',
  104: 'ROI_SDL',
  105: 'J_K_SDL',
  106: 'WITHIN_ZONES_ROS_SDL',
  107: 'WITHIN_ZONES_UP_SDL',
  108: 'ROI_ROS_SDL',
  109: 'ROI_UP_SDL',
  110: 'J_K_ROS_SDL',
  111: 'J_K_UP_SDL',
  112: 'NE_SDL',
  113: 'NE_ROS_SDL',
  114: 'NE_UP_SDL',
}

export const zoneName = (code) => ZONE_CODE_NAMES[Number(code)] || (code == null ? '' : 'ZONE_' + code)

export const isPincode = (v) => /^\d{6}$/.test(String(v || '').trim())

/** TAT range in days: "2–4", "3", "min 2", "max 5", or '' when none is set. */
export const fmtTat = (mi, mx) => {
  const a = mi == null ? null : Number(mi)
  const b = mx == null ? null : Number(mx)
  if (a == null && b == null) return ''
  if (a != null && b != null) return a === b ? String(a) : `${a}–${b}`
  return a != null ? `min ${a}` : `max ${b}`
}

/** Merchant zone name by code (1-5), e.g. "Metro"; any other code falls back to zoneName. */
export const merchantZoneName = (code) => MERCHANT_ZONES.find((z) => z.value === String(code))?.label || zoneName(code)

/** Colour per merchant zone (1 cheapest … 5 dearest): `fg` for text/tiles, `bg` for tints. */
const ZONE_TONES = {
  1: {fg: '#1E7A4E', bg: '#E5F3EB'},
  2: {fg: '#0E7285', bg: '#E0F1F4'},
  3: {fg: '#2F55C4', bg: '#E7EDFC'},
  4: {fg: '#A3570B', bg: '#FBEEDD'},
  5: {fg: '#B3234F', bg: '#FBE7EE'},
}
export const zoneTone = (code) => ZONE_TONES[Number(code)] || {fg: '#475569', bg: '#F1F5F9'}

/* ── Distance (ZM_02 §3) ─────────────────────────────────────────────────────
   Straight-line haversine on the two pins' pincode_master coordinates, R = 6371.0 km,
   rounded once to integer tenths of a km (km10). Compared against the regional distance
   limit: 450 km when both pins are in the same state, 100 km across states (inclusive). */
const REGIONAL_KM = {sameState: 450, crossState: 100}

const R_KM = 6371.0
const DEG = Math.PI / 180.0

/** Raw haversine distance in km (unrounded) — same operations, same order as the spec's reference. */
function haversineKm(lat1, lon1, lat2, lon2) {
  const la1 = lat1 * DEG
  const lo1 = lon1 * DEG
  const la2 = lat2 * DEG
  const lo2 = lon2 * DEG
  const s1 = Math.sin((la2 - la1) / 2)
  const s2 = Math.sin((lo2 - lo1) / 2)
  let h = s1 * s1 + Math.cos(la1) * Math.cos(la2) * (s2 * s2)
  h = Math.min(Math.max(h, 0.0), 1.0)
  return 2 * R_KM * Math.asin(Math.sqrt(h))
}

/** Integer tenths of a km — the only rounding the spec allows. */
const toKm10 = (km) => Math.floor(km * 10.0 + 0.5)

/**
 * Distance facts for a lane from two loaded pin facts (GET tools/pincode/zoneFacts).
 * `state` is pincode_master's own (lowercase), compared exactly for the same-state limit.
 * Returns {ok:false, reason} when the distance is not reliable (a rough or missing location),
 * else {ok:true, kmRaw, km10, sameState, limitKm, within} — km10 is integer tenths (km = km10 / 10).
 */
export function laneDistance(a, b) {
  if (!a || !b) return {ok: false, reason: 'missing'}
  if (a.rough || b.rough) return {ok: false, reason: 'rough'}
  if ([a.latitude, a.longitude, b.latitude, b.longitude].some((v) => typeof v !== 'number')) return {ok: false, reason: 'missing'}
  const kmRaw = haversineKm(a.latitude, a.longitude, b.latitude, b.longitude)
  const km10 = toKm10(kmRaw)
  const sameState = !!a.state && a.state === b.state
  const limitKm = sameState ? REGIONAL_KM.sameState : REGIONAL_KM.crossState
  return {ok: true, kmRaw, km10, sameState, limitKm, within: km10 <= limitKm * 10}
}
